import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetPersonality, apiUpdatePersonality } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X, Heart, Sparkles, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function PersonalityPage() {
  const { profileId } = useParams();
  const { token } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [relazione, setRelazione] = useState("");

  const [carattere, setCarattere] = useState("");
  const [modoParlare, setModoParlare] = useState("");
  const [modiDiDire, setModiDiDire] = useState([""]);
  const [valori, setValori] = useState([""]);
  const [interessi, setInteressi] = useState([""]);
  const [ricordi, setRicordi] = useState([""]);
  const [aneddoti, setAneddoti] = useState([""]);

  useEffect(() => {
    if (!token || !profileId) return;
    apiGetPersonality(token, profileId).then((data) => {
      setProfileName(data.nome);
      setRelazione(data.relazione);
      const p = data.personality || {};
      setCarattere(p.carattere || "");
      setModoParlare(p.modo_parlare || "");
      setModiDiDire(p.modi_di_dire?.length ? p.modi_di_dire : [""]);
      setValori(p.valori?.length ? p.valori : [""]);
      setInteressi(p.interessi?.length ? p.interessi : [""]);
      setRicordi(p.ricordi_chiave?.length ? p.ricordi_chiave : [""]);
      setAneddoti(p.aneddoti?.length ? p.aneddoti : [""]);
    }).catch(() => {
      toast.error("Profilo non trovato");
      nav("/dashboard");
    }).finally(() => setLoading(false));
  }, [token, profileId, nav]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiUpdatePersonality(token, profileId, {
        carattere,
        modo_parlare: modoParlare,
        modi_di_dire: modiDiDire.filter(Boolean),
        valori: valori.filter(Boolean),
        interessi: interessi.filter(Boolean),
        ricordi_chiave: ricordi.filter(Boolean),
        aneddoti: aneddoti.filter(Boolean),
      });
      toast.success("Personalita aggiornata. Le prossime conversazioni saranno piu autentiche.");
    } catch { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  const updateList = (setter, idx, value) => {
    setter((prev) => { const n = [...prev]; n[idx] = value; return n; });
  };
  const addToList = (setter) => setter((prev) => [...prev, ""]);
  const removeFromList = (setter, idx) => setter((prev) => prev.filter((_, i) => i !== idx));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero" data-testid="personality-page">
      <header className="border-b border-border/40 bg-card/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="personality-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <Button variant="premium" size="sm" onClick={handleSave} disabled={saving} data-testid="save-personality">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-1">
            Raccontami di {profileName}
          </h1>
          <p className="text-sm text-muted-foreground">
            Piu dettagli condividi, piu le conversazioni saranno autentiche e fedeli a come era davvero.
          </p>
        </motion.div>

        <div className="space-y-5">
          {/* Character */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="bg-card border-border/30 shadow-soft">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-4 h-4 text-accent" />
                  <h2 className="font-serif text-base font-bold text-foreground">Com'era {profileName}?</h2>
                </div>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Descrivi il suo carattere</Label>
                    <Textarea placeholder={`Es. "${profileName} era una persona solare, sempre pronta a ridere. Aveva un cuore enorme ma non lo dava a vedere..."`}
                      value={carattere} onChange={(e) => setCarattere(e.target.value)} rows={3} data-testid="input-carattere" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Come parlava? Qual era il suo stile?</Label>
                    <Textarea placeholder={`Es. "Parlava con calma, usava sempre il dialetto quando si emozionava. Chiamava tutti 'tesoro mio'..."`}
                      value={modoParlare} onChange={(e) => setModoParlare(e.target.value)} rows={3} data-testid="input-modo-parlare" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Expressions */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-card border-border/30 shadow-soft">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MessageCircle className="w-4 h-4 text-primary" />
                  <h2 className="font-serif text-base font-bold text-foreground">Modi di dire e frasi tipiche</h2>
                </div>
                <p className="text-[11px] text-muted-foreground mb-3">Le frasi che ripeteva sempre, quelle che lo/la rendevano unico/a</p>
                <ListEditor items={modiDiDire} setItems={setModiDiDire}
                  placeholder={`Es. "Dai che ce la facciamo!"`} testPrefix="modi" />
              </CardContent>
            </Card>
          </motion.div>

          {/* Values & Interests */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-card border-border/30 shadow-soft">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="w-4 h-4 text-rose-400" />
                  <h2 className="font-serif text-base font-bold text-foreground">Valori e passioni</h2>
                </div>
                <div className="space-y-5">
                  <div>
                    <Label className="text-xs mb-2 block">Cosa contava di piu per {profileName}?</Label>
                    <ListEditor items={valori} setItems={setValori}
                      placeholder="Es. La famiglia, l'onesta, il lavoro" testPrefix="valori" />
                  </div>
                  <div>
                    <Label className="text-xs mb-2 block">Interessi e passioni</Label>
                    <ListEditor items={interessi} setItems={setInteressi}
                      placeholder="Es. Cucinare, il calcio, la musica" testPrefix="interessi" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Memories */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-card border-border/30 shadow-soft">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="w-4 h-4 text-amber-500" />
                  <h2 className="font-serif text-base font-bold text-foreground">Ricordi e aneddoti</h2>
                </div>
                <div className="space-y-5">
                  <div>
                    <Label className="text-xs mb-2 block">Ricordi importanti che avete condiviso</Label>
                    <ListEditor items={ricordi} setItems={setRicordi}
                      placeholder="Es. Le estati al mare a Rimini quando ero piccolo" testPrefix="ricordi" />
                  </div>
                  <div>
                    <Label className="text-xs mb-2 block">Storie e aneddoti che amava raccontare</Label>
                    <ListEditor items={aneddoti} setItems={setAneddoti}
                      placeholder="Es. Quando ha conosciuto la nonna al ballo del paese" testPrefix="aneddoti" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bottom save */}
        <div className="mt-8 flex justify-end">
          <Button variant="premium" size="lg" onClick={handleSave} disabled={saving} data-testid="save-personality-bottom">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva personalita"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ListEditor({ items, setItems, placeholder, testPrefix }) {
  const update = (idx, val) => setItems((prev) => { const n = [...prev]; n[idx] = val; return n; });
  const add = () => setItems((prev) => [...prev, ""]);
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={item} onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder} className="flex-1" data-testid={`${testPrefix}-${i}`} />
          {items.length > 1 && (
            <button onClick={() => remove(i)} className="text-muted-foreground/30 hover:text-destructive transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
      {items.length < 10 && (
        <button onClick={add} className="text-xs text-accent hover:text-accent/80 transition-colors flex items-center gap-1"
          data-testid={`${testPrefix}-add`}>
          <Plus className="w-3 h-3" /> Aggiungi
        </button>
      )}
    </div>
  );
}
